import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  Autocomplete,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../apiRequest";
import { endPoints } from "../../apis";

// Import the new components
import CategoryFormModal from "./components/subCategory/CategoryFormModal"; // This will handle subcategory forms
import CategoryTable from "./components/subCategory/CategoryTable"; // This will display subcategories
import DeleteDialog from "./components/DeleteDialog";

function CategoryManagement() {
  const [categories, setCategories] = useState([]); // For parent category filter and selection in modal
  const [subCategories, setSubCategories] = useState([]); // The main data for this page
  const [serviceTypes, setServiceTypes] = useState([]); // For modal dropdown
  const [services, setServices] = useState([]); // For modal dropdown
  const [customForms, setCustomForms] = useState([]); // For modal dropdown

  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [currentId, setCurrentId] = useState(null); // ID of subcategory being deleted/edited
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null); // For filtering subcategories
  const [isReordering, setIsReordering] = useState(false); // To disable drag during reorder API call
  const [searchValue, setSearchValue] = useState('');

  const [categoryData, setCategoryData] = useState({
    parent_id: '',
    service_type_id: '',
    service_ids: [],
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    image: '',
    status: '',
    min_price: '',
    double_location: true,
    form_id: '',
    price_per_km: "",

  });

  const initialState = {
    parent_id: '',
    service_type_id: '',
    service_ids: [],
    name_en: '',
    name_fr: '',
    description_en: '',
    description_fr: '',
    image: '',
    status: '',
    min_price: '',
    double_location: true,
    form_id: ''
  };

  // --- Data Fetching Functions ---

  const fetchCustomForms = useCallback(async () => {
    try {
      const params = { limit: 0 };
      const headers = { "x-access-token": accessToken };
      const response = await apiRequest.get(endPoints.GET_FORMS, params, headers);
      if (response.success) {
        setCustomForms(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  }, [accessToken]);

  const fetchParentCategories = useCallback(async () => {
    try {
      const params = { limit: 0 };
      const headers = { "x-access-token": accessToken };
      const response = await apiRequest.get(endPoints.GET_CATEGORIES, params, headers);
      if (response.success) {
        setCategories(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [accessToken]);

  const fetchServices = useCallback(async () => {
    try {
      const params = { limit: 0 };
      const headers = { "x-access-token": accessToken };
      const response = await apiRequest.get(endPoints.GET_SERVICES, params, headers);
      if (response.success) {
        setServices(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, [accessToken]);

  const fetchServiceTypes = useCallback(async () => {
    try {
      const params = { limit: 0 };
      const headers = { "x-access-token": accessToken };
      const response = await apiRequest.get(endPoints.GET_SERVICE_TYPES, params, headers);
      if (response.success) {
        setServiceTypes(response.data.items || []);
      }
    } catch (error) {
      console.error("Error fetching service types:", error);
    }
  }, [accessToken]);

  // Fetch sub-categories with parent filter and pagination
  const getSubCategories = useCallback(async (parentId = null, page = 1, searchValue) => {
    setLoading(true);
    try {
      const params = { page, total: totalItems, search: searchValue };
      if (parentId) params.parent_id = parentId;

      const headers = { "x-access-token": accessToken };
      const response = await apiRequest.get(endPoints.GET_SUB_CATEGORIES, params, headers);

      if (response.success) {
        const { items, pagination } = response.data;
        setSubCategories(items || []);
        setTotalPages(pagination.total_pages);
        setCurrentPage(pagination.current_page);
        setTotalItems(pagination.totalItems);
      }
    } catch (error) {
      console.error("Error fetching sub categories:", error);
      setError("Failed to fetch sub categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, totalItems]);

  // --- Effects ---

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (token) setAccessToken(token);

    // Fetch all lookup data once
    fetchParentCategories();
    fetchCustomForms();
    fetchServiceTypes();
    fetchServices();
  }, [fetchParentCategories, fetchCustomForms, fetchServiceTypes, fetchServices]);

  useEffect(() => {
    // Fetch subcategories when page, parent filter, or search value changes
    getSubCategories(selectedParent, currentPage, searchValue);
  }, [currentPage, selectedParent, getSubCategories, searchValue]);

  // --- Handlers ---

  // Handle parent category filter change
  const handleParentChange = (event, newValue) => {
    setSelectedParent(newValue ? newValue.id : null);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Modal open/close
  const handleOpenModal = () => {
    setOpenModal(true);
    setCategoryData(initialState);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
    setCategoryData(initialState);
    setIsEditing(false);
  };

  // Form input change handler for text fields and switches
  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (type === 'checkbox' || type === 'switch') {
      setCategoryData({
        ...categoryData,
        [name]: checked,
      });
    } else if (name === 'service_ids') { // Special handling for Autocomplete multi-select
      setCategoryData({
        ...categoryData,
        [name]: value, // value here is already the array of IDs from Autocomplete onChange
      });
    }
    else {
      setCategoryData({
        ...categoryData,
        [name]: value,
      });
    }
  };

  // File input change handler
  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setCategoryData((prevState) => ({
        ...prevState,
        [fieldName]: file,
        [`${fieldName}_preview`]: URL.createObjectURL(file),
      }));
    }
  };

  // Form submission (Add/Edit Subcategory)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();

      // Append text fields
      formData.append('name_en', categoryData.name_en);
      formData.append('name_fr', categoryData.name_fr);
      formData.append('description_en', categoryData.description_en);
      formData.append('description_fr', categoryData.description_fr);
      formData.append('min_price', categoryData.min_price || 0);
      formData.append('service_type_id', categoryData.service_type_id);
      formData.append('double_location', categoryData.double_location);
      formData.append('form_id', categoryData.form_id);
      formData.append('is_parent', false);
      formData.append("price_per_km", categoryData.price_per_km || 0);

      // Handle parent_id - support multiple
      if (Array.isArray(categoryData.parent_id)) {
        categoryData.parent_id.forEach((id) => {
          formData.append('parent_id[]', id); // Backend must expect array
        });
      }

      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }
      // return false;


      // Append image if it's a File
      if (categoryData.image instanceof File) {
        formData.append('image', categoryData.image);
      }

      // Append service_ids as JSON string
      if (categoryData.service_ids && Array.isArray(categoryData.service_ids)) {
        formData.append('service_ids', JSON.stringify(categoryData.service_ids));
      }

      // Set endpoint and method
      const endPoint = isEditing
        ? `${endPoints.UPDATE_CATEGORY}/${currentId}`
        : `${endPoints.ADD_CATEGORY}`;

      const requestMethod = isEditing ? apiRequest.put : apiRequest.post;

      const response = await requestMethod(endPoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-access-token': accessToken,
        },
      });

      if (response.success) {
        setSuccess(isEditing ? "Sub Category updated successfully." : "Sub Category added successfully.");
        getSubCategories(selectedParent, currentPage, searchValue);
        handleCloseModal();
      } else {
        setError(response.message || 'Failed to add/update sub category.');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred while processing your request.');
    } finally {
      setLoading(false);
    }
  };

  // Edit handler
  const handleEdit = (subCat) => {
    handleOpenModal();
    setIsEditing(true);

    setCategoryData({
      name_en: subCat.name_en || "",
      name_fr: subCat.name_fr || "",
      description_en: subCat.description_en || "",
      description_fr: subCat.description_fr || "",
      image: subCat.image || null,
      image_preview: subCat.image ? `${apiRequest.BASE_URL}/${subCat.image}` : null,
      parent_id: Array.isArray(subCat.parent_categories)
        ? subCat.parent_categories.map(p => p.id)
        : [],
      service_type_id: subCat.service_type_id || null,
      form_id: subCat.form_id || null,
      min_price: subCat.min_price || '',
      double_location: subCat.double_location || false,
      status: subCat.status || "active",
      service_ids: Array.isArray(subCat.services) ? subCat.services.map(s => s.id) : [],
      price_per_km: subCat.price_per_km ?? "",
    });

    setCurrentId(subCat.id);
  };


  // Handle delete confirmation dialog open
  const handleDeleteClick = (id) => {
    setCurrentId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete action
  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false); // Close the dialog first
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiRequest.delete(
        `${endPoints.DELETE_CATEGORY}/${currentId}`, // Assuming same endpoint for deleting categories/subcategories
        {
          headers: {
            'x-access-token': accessToken,
          },
        }
      );

      if (response.success) {
        setSuccess("Sub Category deleted successfully.");
        setSubCategories((prev) => prev.filter((subCat) => subCat.id !== currentId));
        // Re-fetch to handle pagination shifts if the last item on a page was deleted
        getSubCategories(selectedParent, currentPage, searchValue);
      } else {
        setError(response.message || "Failed to delete the sub category.");
      }
    } catch (error) {
      console.error("Error deleting sub category:", error);
      setError("An error occurred while deleting the sub category.");
    } finally {
      setLoading(false);
    }
  };

  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Handle status toggle for a subcategory
  const handleStatusToggle = async (id, currentStatus) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const payload = { status: newStatus };
      const headers = {
        'Content-Type': 'application/json',
        'x-access-token': accessToken,
      };

      const response = await apiRequest.put(
        `${endPoints.STATUS_CATEGORY}/${id}`, // Assuming same endpoint for status
        payload,
        { headers }
      );

      if (response.success) {
        setSuccess("Sub Category status updated successfully.");
        setSubCategories((prev) =>
          prev.map((subCat) =>
            subCat.id === id ? { ...subCat, status: newStatus } : subCat
          )
        );
      } else {
        setError(response.message || 'Failed to update sub category status.');
      }
    } catch (error) {
      console.error('Error updating sub category status:', error);
      setError('An error occurred while updating the sub category status.');
    } finally {
      setLoading(false);
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = async (result) => {
    setIsReordering(false); // Reset immediately

    if (!result.destination || !selectedParent) return; // Only reorder if a parent is selected

    const items = Array.from(subCategories);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update local state immediately for smooth UI
    setSubCategories(items);

    try {
      setLoading(true); // Show loading spinner
      const headers = {
        "x-access-token": accessToken,
        "Content-Type": "application/json"
      };

      // Prepare the sequence data for the API call
      const sequenceData = items.map((item, index) => ({
        id: item.id,
        sequence: index + 1 // Sequence typically starts from 1
      }));

      const response = await apiRequest.put(
        endPoints.SEQUENCE_CATEGORY, // Assuming this endpoint handles subcategory sequence
        sequenceData,
        { headers }
      );

      if (response.success) {
        setSuccess("Sub Category order updated successfully!");
      } else {
        setError(response.message || "Failed to save new order. Please try again.");
        // Revert to original order if API fails
        getSubCategories(selectedParent, currentPage, searchValue);
      }
    } catch (error) {
      console.error("Error updating sequence:", error);
      setError("An error occurred while updating the sequence.");
      getSubCategories(selectedParent, currentPage, searchValue); // Revert on network error
    } finally {
      setLoading(false);
    }
  };

  // Set reordering state when drag starts
  //   const handleDragStart = () => {
  //     setIsReordering(true);
  //   };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Sub Categories
                </MDTypography>
              </MDBox>
              <MDBox pt={1} px={1}>
                <MDBox
                  pt={1}
                  px={1}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={2}
                  flexWrap="wrap"
                
                  
                >
                  {/* Parent Category Filter */}
                  <Autocomplete
                    options={categories}
                    getOptionLabel={(option) => option?.name_en || ""}
                    value={categories.find(c => c.id === selectedParent) || null}
                    onChange={handleParentChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Filter by Parent Category"
                        variant="outlined"
                       
                         sx={{
                      width: 220,
                      // height: 39, // 🟢 Equal height
                      "& .MuiInputBase-root": { height: "39px" }, // 🟢 Match input box height
                    }}
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                  />

                  <TextField
                    label="Search"
                    variant="outlined"
                    placeholder="Search by title or description"
                    style={{ width: 220 }}
                    value={searchValue}
                    sx={{
                      width: 220,
                      height: 37, // 🟢 Equal height
                      "& .MuiInputBase-root": { height: "37px" }, // 🟢 Match input box height
                    }}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleOpenModal}
                    style={{ color: "white" }}
                  >
                    Add Sub Category
                  </Button>
                </MDBox>

                <CategoryTable
                  subCategories={subCategories}
                  selectedParent={selectedParent}
                  isReordering={isReordering}
                  handleDragEnd={handleDragEnd}
                  handleStatusToggle={handleStatusToggle}
                  handleEdit={handleEdit}
                  handleDeleteClick={handleDeleteClick}
                />

                {/* Pagination Controls */}
                <MDBox mt={2} display="flex" justifyContent="center" alignItems="center">
                  <Button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <MDTypography mx={2}>
                    Page {currentPage} of {totalPages}
                  </MDTypography>
                  <Button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modal for Adding/Editing Sub Category */}
      <CategoryFormModal
        open={openModal}
        handleClose={handleCloseModal}
        categoryData={categoryData}
        handleInputChange={handleInputChange}
        handleFileChange={handleFileChange}
        handleSubmit={handleSubmit}
        isEditing={isEditing}
        categories={categories} // Pass parent categories to the modal
        serviceTypes={serviceTypes}
        services={services}
        customForms={customForms}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteDialog
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        handleDeleteConfirm={handleDeleteConfirm}
      />

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error/Success Snackbar */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: '100%' }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default CategoryManagement;